import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { api, type CreditBalance } from '../../lib/api';
import { formatCredits } from '@/lib/format-credits';

/**
 * Persistent banner for Guest Trial Mode sessions.
 *
 * Shows the remaining trial balance (the SYSTEM bucket — where guest trial
 * credits live; the normal CreditBalanceIndicator deliberately excludes system
 * and would show a misleading "0" for a guest) plus days left, and nudges the
 * visitor toward creating an account. Renders nothing for non-guests.
 */
export function GuestTrialBanner() {
  const { isGuest, user } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);

  useEffect(() => {
    if (!isGuest) return;
    const load = () => {
      api
        .getCreditBalance()
        .then(setBalance)
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [isGuest]);

  if (!isGuest) return null;

  const trialCredits = balance ? Math.max(0, Math.round(balance.buckets.system)) : null;
  const daysLeft = user?.expiresAt
    ? Math.max(0, Math.ceil((new Date(user.expiresAt).getTime() - Date.now()) / 86_400_000))
    : null;

  return (
    <div className="w-full bg-primary/10 border-b border-primary/20 text-sm">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-2">
        <span className="text-foreground">
          You&rsquo;re exploring Fias as a guest
          {trialCredits !== null && (
            <>
              {' · '}
              <strong>{formatCredits(trialCredits)}</strong> trial credits left
            </>
          )}
          {daysLeft !== null && (
            <>
              {' · '}
              {daysLeft} day{daysLeft === 1 ? '' : 's'} left
            </>
          )}
        </span>
        <Link
          to="/auth/signup"
          className="shrink-0 rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Create an account to keep your work
        </Link>
      </div>
    </div>
  );
}
